import { useState } from 'react';

interface UploadTemplateConfig {
  title?: string;
  description?: string;
  acceptedFileTypes?: string[];
  maxFileSize?: number;
  templateFile?: string;
  templateFileName?: string;
  showFileInput?: boolean;
  customTemplateContent?: React.ReactNode;
}

interface UseUploadTemplateReturn {
  isModalOpen: boolean;
  openModal: (config?: UploadTemplateConfig) => void;
  closeModal: () => void;
  modalConfig: UploadTemplateConfig;
  handleUpload: (files: FileList | null) => void;
}

export const useUploadTemplate = (
  onUpload: (files: FileList | null) => void,
  defaultConfig?: UploadTemplateConfig
): UseUploadTemplateReturn => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState<UploadTemplateConfig>({
    title: 'Upload Template Guide',
    description: 'Please follow the template structure below to ensure your upload is processed correctly.',
    acceptedFileTypes: ['.xlsx', '.xls', '.pdf', '.doc', '.docx'],
    maxFileSize: 10,
    templateFile: undefined,
    templateFileName: 'Template.xlsx',
    showFileInput: true,
    ...defaultConfig
  });

  const openModal = (config?: UploadTemplateConfig) => {
    if (config) {
      setModalConfig(prev => ({ ...prev, ...config }));
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleUpload = (files: FileList | null) => {
    onUpload(files);
    closeModal();
  };

  return {
    isModalOpen,
    openModal,
    closeModal,
    modalConfig,
    handleUpload
  };
};

// Predefined configurations for common upload scenarios
export const uploadConfigs = {
  excel: {
    title: 'Excel Upload Template',
    description: 'Upload Excel files following the template structure. Ensure all required columns are present and data is properly formatted.',
    acceptedFileTypes: ['.xlsx', '.xls'],
    maxFileSize: 5,
    templateFile: '/templates/Survey_Questions.xlsx',
    templateFileName: 'Excel_Template.xlsx'
  },
  
  documents: {
    title: 'Document Upload Template',
    description: 'Upload documents in supported formats. Ensure files are properly named and contain the required information.',
    acceptedFileTypes: ['.pdf', '.doc', '.docx'],
    maxFileSize: 10,
    templateFile: '/templates/Document_Template.docx',
    templateFileName: 'Document_Template.docx'
  },
  
  images: {
    title: 'Image Upload Template',
    description: 'Upload images in supported formats. Ensure images are clear and meet the required specifications.',
    acceptedFileTypes: ['.jpg', '.jpeg', '.png', '.gif'],
    maxFileSize: 5,
    templateFile: undefined,
    templateFileName: 'Image_Guidelines.pdf'
  },
  
  certificates: {
    title: 'Certificate Upload Template',
    description: 'Upload certificate images. Ensure the certificate is clearly visible and contains all required information.',
    acceptedFileTypes: ['.jpg', '.jpeg', '.png'],
    maxFileSize: 5,
    templateFile: undefined,
    templateFileName: 'Certificate_Template.png'
  },
  
  announcements: {
    title: 'Announcement File Upload',
    description: 'Upload files to be attached to announcements. Supported formats include documents and images.',
    acceptedFileTypes: ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'],
    maxFileSize: 10,
    templateFile: undefined,
    templateFileName: 'Announcement_Guidelines.pdf'
  },
  
  institutions: {
    title: 'Institution Registration Template',
    description: 'Upload Excel files containing institution data for bulk registration. Ensure all required fields are properly filled and the data format matches the template structure.',
    acceptedFileTypes: ['.xlsx', '.xls'],
    maxFileSize: 5,
    templateFile: '/templates/Organizations_Template.xlsx',
    templateFileName: 'Organizations_Template.xlsx'
  }
}; 